import React, { useEffect, useState, useCallback } from "react";
import Modal from "./Modal";
import TextboxSet from "./TextboxSet";
import DropdownSet from "./DropdownSet";
import Button from "./Button";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Clock, InfoCircle, TickCircle } from "iconsax-react";
import { salutations } from "../Constant/salutation";
import axios from "axios";
import {
  GUEST_CREATE_API,
  DIETARY_OPTIONS_API,
  AVAILABLE_TABLE_OPTIONS_API,
  TAG_OPTIONS_API,
} from "../Constant/api";
import { useToast } from "./Toast";

const AddGuestModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [tableList, setTableList] = useState([]);
  const [dietaryList, setDietaryList] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [guestInfo, setGuestInfo] = useState({
    salutation: "",
    first_name: "",
    last_name: "",
    email: "",
    contact: "",
    table: "",
    dietary: [],
    tags: [],
    note: "",
    status: false,
    group_name: "",
  });

  const { toast } = useToast();

  const fetchDietaryList = useCallback(() => {
    axios
      .get(DIETARY_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((res) => {
        setDietaryList(res.data.data);
      })
      .catch((err) => {
        console.log(err);
        toast("Failed to fetch dietary list.", "error");
      });
  }, [toast]);

  const fetchTagList = useCallback(() => {
    axios
      .get(TAG_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((res) => {
        setTagList(res.data.data);
      })
      .catch((err) => {
        console.log(err);
        toast("Failed to fetch tag list.", "error");
      });
  }, [toast]);

  const fetchTableList = useCallback(() => {
    axios
      .get(AVAILABLE_TABLE_OPTIONS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((res) => {
        setTableList(res.data.data);
      })
      .catch((err) => {
        console.log(err);
        toast("Failed to fetch table list.", "error");
      });
  }, [toast]);

  const handleCreate = () => {
    if (!guestInfo.first_name || !guestInfo.last_name) {
      toast("First name and last name are required.", "error");
      return;
    }

    setLoading(true);
    axios
      .post(
        GUEST_CREATE_API,
        {
          guests: [
            {
              id: "guest-0",
              ...guestInfo,
            },
          ],
          group_name: guestInfo.group_name,
          eventId: sessionStorage.getItem("event_id"),
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        },
      )
      .then((res) => {
        setLoading(false);
        toast("Guest added successfully.", "success");
        handleClose();
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        toast(
          err.response?.data?.message || "Failed to create guest.",
          "error",
        );
      });
  };

  const handleOnChange = (event) => {
    const { name, value } = event.target;
    setGuestInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDropDownChange = (name, value) => {
    setGuestInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClose = () => {
    setGuestInfo({
      salutation: "",
      first_name: "",
      last_name: "",
      email: "",
      contact: "",
      table: "",
      dietary: [],
      tags: [],
      note: "",
      status: false,
      group_name: "",
    });
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      fetchTableList();
      fetchDietaryList();
      fetchTagList();
    }
  }, [isOpen, fetchTableList, fetchDietaryList, fetchTagList]);

  if (!isOpen) return null;

  return (
    <Modal
      toggleModal={handleClose}
      title="Add Guest"
      handleSubmit={handleCreate}
      submitLabel="Create"
      loading={loading}
    >
      <div className="flex flex-col w-full gap-[16px]">
        {/* Group Name */}
        <div className="flex flex-col w-full gap-[8px]">
          <h2 className="text-[14px]">Group Name</h2>
          <div className="w-full px-[12px] py-[11px] border border-[#EFF0F2] rounded-xl">
            <input
              type="text"
              name="group_name"
              value={guestInfo.group_name}
              className="w-full focus:outline-none"
              onChange={handleOnChange}
            />
          </div>
          <p className="flex text-[#A1A7B2] text-[11px] gap-[4px] items-center">
            <InfoCircle size="11" variant="Bold" />
            Leave this field empty if you only want to add individual guest
          </p>
        </div>

        {/* Salutation */}
        <DropdownSet
          value={guestInfo.salutation}
          options={salutations}
          label="Title/Salutation"
          name="salutation"
          onChange={handleDropDownChange}
        />

        {/* First & Last Name */}
        <div className="grid grid-cols-2 gap-[16px]">
          <TextboxSet
            type="text"
            name="first_name"
            value={guestInfo.first_name}
            label="First Name"
            required
            className="w-full"
            onChange={handleOnChange}
          />
          <TextboxSet
            type="text"
            name="last_name"
            value={guestInfo.last_name}
            label="Last Name"
            required
            className="w-full"
            onChange={handleOnChange}
          />
        </div>

        {/* Email */}
        <TextboxSet
          type="email"
          name="email"
          value={guestInfo.email}
          label="Email"
          className="w-full"
          onChange={handleOnChange}
        />

        {/* Contact Number */}
        <div className="flex flex-col w-full gap-[6px]">
          <h2>Contact Number</h2>
          <PhoneInput
            country={"my"}
            value={guestInfo.contact}
            inputStyle={{
              width: "100%",
              height: "38px",
              borderRadius: "8px",
              fontSize: "14px",
              borderColor: "#EFF0F2",
            }}
            buttonStyle={{
              borderRadius: "8px",
              borderColor: "#EFF0F2",
            }}
            onChange={(phone) =>
              setGuestInfo((prev) => ({
                ...prev,
                contact: phone,
              }))
            }
          />
        </div>

        {/* Table Number */}
        <DropdownSet
          value={guestInfo.table}
          options={tableList}
          label="Table Number"
          name="table"
          onChange={handleDropDownChange}
        />

        {/* Special Dietary */}
        <DropdownSet
          value={guestInfo.dietary}
          options={dietaryList}
          label="Special Dietary"
          name="dietary"
          isMulti
          onChange={handleDropDownChange}
        />

        {/* Tags */}
        <DropdownSet
          value={guestInfo.tags}
          options={tagList}
          label="Tags"
          name="tags"
          isMulti
          onChange={handleDropDownChange}
        />

        {/* Note */}
        <TextboxSet
          type="textarea"
          name="note"
          value={guestInfo.note}
          label="Note"
          className="w-full"
          onChange={handleOnChange}
        />

        {/* Check-In Status */}
        <div className="flex flex-col w-full gap-[8px]">
          <h2>
            Check-In Status
            <span className="text-red-500">*</span>
          </h2>
          <div className="flex gap-[16px]">
            <Button
              className="flex w-full items-center gap-[8px]"
              variant={guestInfo.status === false ? "primary" : "secondary"}
              onClick={() =>
                setGuestInfo((prev) => ({ ...prev, status: false }))
              }
            >
              <Clock size="16" variant="Bold" />
              Pending
            </Button>
            <Button
              className="flex w-full items-center gap-[8px]"
              variant={guestInfo.status === true ? "primary" : "secondary"}
              onClick={() =>
                setGuestInfo((prev) => ({ ...prev, status: true }))
              }
            >
              <TickCircle size="16" variant="Bold" />
              Checked
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddGuestModal;
